import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";

import { useGetBlogPost, useLikeBlogPost } from "@Hooks/useLenderBlogPosts";
import { countWords, formatDate, stripHtml } from "@Utils/functions";
import VideoPlayer from "@Components/VideoPlayer/VideoPlayer";

import LikeButton from "./LikeButton";
import styles from "./BlogPostDetail.module.scss";

// Older posts were authored as plain text (paragraphs separated by a
// blank line); newer posts are authored as HTML. Detect which one we
// have so both keep rendering correctly without a content migration.
const isHtml = (body: string) => /<[a-z][\s\S]*>/i.test(body);

const BlogPostDetail: React.FC = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const { blog: post, isLoading, error } = useGetBlogPost(id as string);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (post?.data.info.title) {
      document.title = `${post.data.info.title}`;
    }

    return () => {
      document.title = "WebFinanceDirect Lender";
    };
  }, [post]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Post Not Found</h2>
          <p>
            Sorry, we couldn&#39;t find the blog post you&#39;re looking for.
          </p>
          <Link to="/release-notes" className={styles.backButton}>
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <article className={styles.blogPost}>
        <Link to="/release-notes" className={styles.backLink}>
          <svg
            className={styles.backIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to all posts
        </Link>

        <header className={styles.header}>
          <h1 className={styles.title}>{post.data.info.title}</h1>
          <div className={styles.meta}>
            <div className={styles.postInfo}>
              <span className={styles.date}>
                {formatDate(
                  new Date(post.data.info.publishedAt || post.createdAt),
                  "medium"
                )}
              </span>
              <span className={styles.bullet}>•</span>
              <span className={styles.wordCount}>
                {countWords(stripHtml(post.data.info.body ?? ""))} words
              </span>
              <span className={styles.bullet}>•</span>
              <span className={styles.readTime}>3 min read</span>
            </div>
            <LikeButton
              entity={post}
              className={styles.likeButton}
              useLike={useLikeBlogPost}
            />
          </div>
        </header>

        {post.data.video?.data.info.videoUrl && (
          <div className="added-videos">
            <h4>{`Video Description: ${
              post.data.video?.data.info.description ?? ""
            }`}</h4>
            <VideoPlayer publicUrl={post.data.video?.data.info.videoUrl} />
          </div>
        )}

        <footer className={styles.footer}>
          <div className={styles.content}>
            {isHtml(post.data.info.body ?? "") ? (
              <div
                className={styles.postContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(post.data.info.body ?? ""),
                }}
              />
            ) : (
              <div className={styles.postContent}>
                {post.data.info.body?.split("\n\n").map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            )}

            {post.data.info.tags && post.data.info.tags.length > 0 && (
              <div className={styles.tags}>
                {post.data.info.tags.map((tag) => (
                  <div key={tag} className={styles.tag}>
                    #{tag}
                  </div>
                ))}
              </div>
            )}
          </div>
        </footer>
      </article>
    </div>
  );
};

export default BlogPostDetail;
